import { useUser, useWorkTaskOrderModuleParameters } from "@tm/context-distribution"
import { useParams } from "react-router"
import { BasketBundleParams, ECounterType, PriceType, UserModuleType } from "@tm/models"
import { Box, Icon, LinkButton } from "@tm/components"
import { TmaHelper, renderRoute } from "@tm/utils"
import Morpheus from "@tm/morpheus"
import { useLocalization } from "@tm/localization"
import { ReactNode, useMemo, useState } from "react"
import { OrderCalculation } from "../../../../data/model"
import BasketTotalsTable from "./BasketTotalsTable"
import { LoaderSmall, StyledBox, SummaryPopover } from "../../../StyledComponents"
import { SummaryRouteProps } from "../../component"
import ErrorButton from "../../../_shared/ErrorButton"
import { useSummaryTheme } from "../../hooks/useSummaryTheme"

type Props = {
    workTaskId: string
    calculatedOrder?: OrderCalculation
    partsBasketRoute: string
    hasErrors: boolean
    isLoading: boolean
    additionalErrorTextId?: number
}

export function BasketTotals({ workTaskId, calculatedOrder, partsBasketRoute, hasErrors, isLoading, additionalErrorTextId }: Props) {
    const { userSettings, userContext } = useUser() ?? {}
    const { formatCurrency } = useLocalization()
    const matchParams = useParams<SummaryRouteProps>()
    const { hasWorkTaskOrder } = useWorkTaskOrderModuleParameters()
    const { linkButtonFontWeight, linkButtonFontFamily, linkButtonTextColor, contrastColor, linkButtonSize, linkButtonVariant } = useSummaryTheme()

    const [popoverAnchor, setPopoverAnchor] = useState<HTMLDivElement>()

    const showPartsRetailPrice =
        !userSettings?.showPurchasePrice ||
        !(userSettings?.showPurchasePriceInSummary ?? !Morpheus.getParams<BasketBundleParams>("basket").showRetailPriceInSummaryByDefault)

    const priceType = showPartsRetailPrice ? PriceType.Retail : PriceType.Purchase

    const totalPartsPrice = useMemo(() => {
        const prices = calculatedOrder?.erpTotalsInOrder ? Object.values(calculatedOrder.erpTotalsInOrder) : []
        const purchasePrice = prices.find((p) => p?.type === PriceType.Purchase)
        const retailPrice = prices.find((p) => p?.type === PriceType.Retail) ?? prices.find((p) => p?.type === PriceType.RecommendedRetail)
        return showPartsRetailPrice || !purchasePrice ? retailPrice : purchasePrice
    }, [calculatedOrder?.erpTotalsInOrder, showPartsRetailPrice])

    const partsBasketUrl = renderRoute(partsBasketRoute, matchParams)
    const currencyCodeFallback = userContext?.modules?.find((x) => x.type === UserModuleType.Catalog)?.currencyCode ?? "-"

    const basketButtonContent: ReactNode = useMemo(() => {
        if (hasErrors) {
            return <ErrorButton basketWithErrors workTaskIds={[workTaskId]} additionalErrorTextId={additionalErrorTextId} />
        }

        if (isLoading) {
            return <LoaderSmall />
        }

        return `${formatCurrency(totalPartsPrice?.value ?? 0, totalPartsPrice?.currencyCode_Iso_4217 ?? currencyCodeFallback)}*`
    }, [
        additionalErrorTextId,
        formatCurrency,
        currencyCodeFallback,
        hasErrors,
        isLoading,
        totalPartsPrice?.currencyCode_Iso_4217,
        totalPartsPrice?.value,
        workTaskId,
    ])

    const resolvedTextColor = showPartsRetailPrice || !totalPartsPrice ? "" : `${linkButtonTextColor || ""} !important`

    if (!hasWorkTaskOrder) {
        return null
    }

    return (
        <Box paddingRight={1} alignItems="center">
            <div onMouseEnter={(e) => setPopoverAnchor(e.currentTarget)} onMouseLeave={() => setPopoverAnchor(undefined)}>
                <LinkButton
                    sx={{
                        borderRadius: 0,
                        border: 0,
                        fontWeight: showPartsRetailPrice || !totalPartsPrice ? linkButtonFontWeight : 600,
                        fontFamily: linkButtonFontFamily,
                        color: resolvedTextColor,
                    }}
                    variant={linkButtonVariant}
                    startIcon={<Icon name="cart-summary" color={contrastColor} />}
                    to={partsBasketUrl}
                    onClick={() => TmaHelper.GeneralCountEvent.Call(ECounterType.TopBarBasket)}
                    size={linkButtonSize || "extralarge"}
                >
                    {basketButtonContent}
                </LinkButton>
                {calculatedOrder && !hasErrors && (
                    <SummaryPopover open={!!popoverAnchor} anchorEl={popoverAnchor}>
                        <StyledBox>
                            <BasketTotalsTable calculatedOrder={calculatedOrder} priceType={priceType} />
                        </StyledBox>
                    </SummaryPopover>
                )}
            </div>
        </Box>
    )
}
